'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, cn, motion } from '@/libs';

import { Icon } from '../Icon';
import { Spinner } from '../Spinner';

export type SelectOption<T extends string | number = string> = {
  value: T;
  label: string | number;
  icon?: React.ComponentType<{ className?: string; width?: number; height?: number }>;
  disabled?: boolean;
};

export type SelectProps<T extends string | number = string> = {
  options: SelectOption<T>[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  name?: string;

  loading?: boolean;
  disabled?: boolean;
  isLoading?: boolean;

  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  inputClassName?: string;
  contentClassName?: string;
  optionClassName?: string;

  closeOnSelect?: boolean;
  onOpenChange?: (open: boolean) => void;

  searchable?: boolean;
  onSearch?: (query: string) => void;

  tabIndex?: number;

  renderOption?: (option: SelectOption<T>, isSelected: boolean) => React.ReactNode;
  renderTrigger?: (
    selectedOption: SelectOption<T> | undefined,
    isOpen: boolean,
    loading: boolean,
  ) => React.ReactNode;
};
// TODO: refatorar todo o select
export const Select = <T extends string | number = string>({
  options,
  value,
  defaultValue,
  onChange,
  name,
  loading = false,
  disabled = false,
  isLoading = false,
  placeholder,
  className,
  triggerClassName,
  inputClassName,
  contentClassName,
  optionClassName,
  closeOnSelect = true,
  onOpenChange,
  searchable = false,
  onSearch,
  renderOption,
  renderTrigger,
  tabIndex = -1,
}: SelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<T | undefined>(
    value || defaultValue,
  );
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastSearchQueryRef = useRef<string>('');

  const selectedOption = options.find((option) => option.value === selectedValue);

  const isValidValue = useMemo(
    () => searchable && selectedOption && searchQuery === String(selectedOption.label),
    [searchable, selectedOption, searchQuery],
  );

  useEffect(() => {
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value);
      if (searchable) {
        const option = options.find((opt) => opt.value === value);
        if (option) {
          const newQuery = String(option.label);
          setSearchQuery(newQuery);
          lastSearchQueryRef.current = newQuery;
        } else {
          setSearchQuery('');
          lastSearchQueryRef.current = '';
        }
      }
    }
  }, [value, selectedValue, options, searchable]);

  useEffect(() => {
    if (searchable && onSearch && searchQuery !== lastSearchQueryRef.current) {
      lastSearchQueryRef.current = searchQuery;
      onSearch(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchable]);

  const handleChange = (option: SelectOption<T>) => {
    if (option.disabled) return;

    if (option.value === selectedValue) {
      if (closeOnSelect) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
      return;
    }

    setSelectionLoading(true);
    setSelectedValue(option.value);

    if (searchable) {
      const newQuery = String(option.label);
      setSearchQuery(newQuery);
      lastSearchQueryRef.current = newQuery;
    }

    if (onChange) {
      onChange(option.value);
    }

    if (closeOnSelect) {
      setIsOpen(false);
      onOpenChange?.(false);
    }

    setTimeout(() => {
      setSelectionLoading(false);
    }, 300);
  };

  const handleOpenChange = (open: boolean) => {
    if (disabled || loading || isLoading || selectionLoading) return;

    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleInputChange = (text: string) => {
    if (!searchable) return;
    setSearchQuery(text);
    if (!isOpen && text.length > 0) {
      setIsOpen(true);
      onOpenChange?.(true);
    }
  };

  const handleBlurOrClose = useCallback(() => {
    if (!searchable) return;

    if (searchQuery.trim() === '') {
      setSelectedValue(undefined);
      setSearchQuery('');
      lastSearchQueryRef.current = '';
      const lastValue = value ?? defaultValue ?? '';
      if (onChange && typeof lastValue === 'string' && lastValue !== '') {
        onChange('' as T);
      }
    } else {
      const matchingOption = options.find(
        (opt) =>
          String(opt.label).toLowerCase().trim() === searchQuery.toLowerCase().trim(),
      );

      if (matchingOption) {
        setSelectedValue(matchingOption.value);
        const restoredQuery = String(matchingOption.label);
        setSearchQuery(restoredQuery);
        lastSearchQueryRef.current = restoredQuery;
        if (onChange) {
          onChange(matchingOption.value);
        }
      } else {
        setSelectedValue(undefined);
        setSearchQuery('');
        lastSearchQueryRef.current = '';
        const lastValue = value ?? defaultValue ?? '';
        if (onChange && typeof lastValue === 'string' && lastValue !== '') {
          onChange('' as T);
        }
      }
    }
    setIsOpen(false);
    onOpenChange?.(false);
  }, [searchable, searchQuery, options, value, defaultValue, onChange, onOpenChange]);

  const handleInputFocus = () => {
    if (!searchable) return;
    if (!disabled && !loading && !isLoading && !selectionLoading) {
      setIsOpen(true);
      onOpenChange?.(true);
      if (onSearch) {
        if (isValidValue && lastSearchQueryRef.current !== '') {
          lastSearchQueryRef.current = '';
          onSearch('');
        } else if (searchQuery.trim() === '') {
          lastSearchQueryRef.current = '';
          onSearch('');
        }
      }
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 0);
    }
  };

  const handleInputBlur = () => {
    if (!searchable) return;
    setTimeout(() => {
      handleBlurOrClose();
    }, 200);
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        handleBlurOrClose();
      }
    },
    [handleBlurOrClose],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      const enabledOptions = options.filter((opt) => !opt.disabled);

      if (e.key === 'Escape') {
        if (searchable) {
          handleBlurOrClose();
        } else {
          setIsOpen(false);
          onOpenChange?.(false);
        }
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();

        if (!enabledOptions.length) return;

        const currentIndex = selectedValue
          ? enabledOptions.findIndex((opt) => opt.value === selectedValue)
          : -1;

        let nextIndex;
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < enabledOptions.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : enabledOptions.length - 1;
        }

        const nextOption = enabledOptions[nextIndex];
        if (searchable) {
          setSelectedValue(nextOption.value);
          setSearchQuery(String(nextOption.label));
          if (onChange) {
            onChange(nextOption.value);
          }
        } else {
          handleChange(nextOption);
        }
      }

      if (e.key === 'Enter' && isOpen) {
        e.preventDefault();
        if (selectedValue) {
          if (searchable) {
            const option = enabledOptions.find(
              (opt: SelectOption<T>) => opt.value === selectedValue,
            );
            if (option) {
              handleChange(option);
            }
          } else {
            setIsOpen(false);
            onOpenChange?.(false);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options, selectedValue, onOpenChange, searchable, handleBlurOrClose]);

  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  const isLoadingState = loading || isLoading || selectionLoading;

  const displayValue = searchable
    ? isOpen
      ? searchQuery
      : selectedOption
        ? String(selectedOption.label)
        : searchQuery || ''
    : undefined;

  return (
    <div
      className={cn('relative', searchable ? 'w-full' : 'w-fit', className)}
      ref={selectRef}
      onClick={searchable ? undefined : stopPropagation}
      onPointerDown={searchable ? undefined : stopPropagation}
    >
      {/* Hidden native select for form submission */}
      {name && (
        <select
          name={name}
          value={selectedValue as string}
          onChange={() => {}}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        >
          {options.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {searchable ? (
        <div className="relative">
          <input
            ref={inputRef}
            value={displayValue ?? ''}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled || isLoadingState}
            className={cn(
              'flex w-full border-2 caret-foreground bg-background rounded-md border-foreground/25 px-2 h-11 text-base transition-all placeholder:text-muted-foreground ring-offset-background outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 disabled:text-foreground/40 disabled:cursor-not-allowed md:text-sm',
              inputClassName || triggerClassName,
            )}
            tabIndex={tabIndex}
          />
          {!isLoadingState && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon.ChevronDown className="h-3 w-3 opacity-50" />
            </div>
          )}
          {isLoadingState && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Spinner size={16} />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => handleOpenChange(!isOpen)}
          disabled={disabled || isLoadingState}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled || isLoadingState}
          tabIndex={tabIndex}
          className={cn(
            'border-2 caret-foreground bg-background rounded-md border-foreground/25 h-11 transition-all placeholder:text-muted-foreground flex items-center justify-between w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring gap-3',
            disabled || isLoadingState
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer',
            triggerClassName,
          )}
        >
          {renderTrigger ? (
            renderTrigger(selectedOption, isOpen, isLoadingState)
          ) : (
            <>
              <div className="flex items-center gap-2 truncate">
                {selectedOption?.icon && (
                  <selectedOption.icon width={20} className="flex-shrink-0" />
                )}
                <span className="truncate">
                  {selectedOption ? selectedOption.label : placeholder}
                </span>
              </div>

              {isLoadingState ? (
                <Spinner size={16} />
              ) : (
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Icon.ChevronDown className="h-3 w-3 opacity-50" />
                </motion.div>
              )}
            </>
          )}
        </button>
      )}

      {/* Dropdown Menu with Animation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute left-0 right-0 mt-1 py-1 bg-background rounded shadow-lg z-[9999]',
              searchable && 'border-2 border-foreground/25 rounded-md',
              contentClassName,
            )}
            role="listbox"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No options available
              </div>
            ) : (
              options.map((option) => {
                const isSelected = option.value === selectedValue;

                return (
                  <div
                    key={String(option.value)}
                    className={cn(
                      'flex items-center text-left gap-2 px-3 py-2 text-sm',
                      option.disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:bg-foreground/10',
                      isSelected
                        ? searchable
                          ? 'bg-foreground/5'
                          : 'bg-background'
                        : '',
                      optionClassName,
                    )}
                    onMouseDown={(e) => {
                      if (searchable) {
                        e.preventDefault();
                      }
                      if (!option.disabled) {
                        handleChange(option);
                      }
                    }}
                    onClick={
                      searchable
                        ? undefined
                        : () => !option.disabled && handleChange(option)
                    }
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled}
                  >
                    {renderOption ? (
                      renderOption(option, isSelected)
                    ) : (
                      <>
                        {option.icon && (
                          <option.icon width={20} className="flex-shrink-0" />
                        )}
                        <span className="flex-grow truncate">{option.label}</span>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="ml-auto"
                          >
                            <Icon.Check className="h-3 w-3" />
                          </motion.div>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
